import { Container, Divider, Stack, Typography, styled, useMediaQuery } from "@mui/material";
import {ScrollMenu, VisibilityContext} from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css'; 
import './canvas-controls.css';
import PerimeterControl from "./PerimeterControl";
import ZoomControl from "./ZoomControl";
import AlignControl from "./AlignControl";
import MirrorControl from "./MirrorControl";
import RotateControl from "./RotateControl";
import l10n from "@src/v3.0.0/lib/l10n";
import { useCallback, useContext, useRef } from "react";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
const canvasControlsPermanent = [
  {
    // title: "Misure",
    component: <PerimeterControl />,
    disabled: false,
  },
];

export const canvasControlsHidden = [
  {
    title: "Zoom",
    component: <ZoomControl />,
    disabled: l10n.wppr_cjs_ctrl_zoom_active === "no",
  },
  {
    title: "Allinea",
    component: <AlignControl />,
    disabled: l10n.wppr_cjs_ctrl_position_active === "no",
  },
  {
    title: "Specchia",
    component: <MirrorControl />,
    disabled: l10n.wppr_cjs_ctrl_mirror_active === "no",
  },
  {
    title: "Ruota",
    component: <RotateControl />,
    disabled: l10n.wppr_cjs_ctrl_rotation_active === "no",
  },
];

const labelStyles = {
  display: { 
    xs: "none",
    md: "inline-flex",
  },
  color: 'palette.text.secondary', 
  mr: 1,
  fontSize: '0.8em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

function CanvasControls() {
  const isSM = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const allTools = !isSM
    ? canvasControlsPermanent.concat(canvasControlsHidden).filter((tool) => !tool.disabled)
    : canvasControlsPermanent;
  
  const dragState = useRef(new DragDealer());
  const onMouseDown = useCallback(
    () => dragState.current.dragStart,
    [dragState]
  );
  const onMouseUp = useCallback(
    () => dragState.current.dragStop,
    [dragState],
  );
  const handleDrag = ({ scrollContainer }) => (ev) =>
    dragState.current.dragMove(ev, (posDiff) => {
      if (scrollContainer.current) {
        scrollContainer.current.scrollLeft += posDiff;
      }});

  return (
    <Container>
      <NoScrollbar>
        <ScrollMenu 
          scrollContainerClassName={'horizontal-scrolling-menu-centered'}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={handleDrag}
          onWheel={onWheel}
          LeftArrow={LeftArrow}
          RightArrow={RightArrow}
        >
          {allTools.map(({ title, disabled, component}, key) => !disabled && (
            <MenuItem key={key}>
              {title && <Typography sx={labelStyles}>{title}</Typography>}
              {component}
              {key < allTools.length - 1 && (
                <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 0 }} />
              )}
            </MenuItem>
          ))}
        </ScrollMenu>
      </NoScrollbar>
    </Container>
  );
}

const MenuItem = styled('div')({
  display: 'flex',
  placeItems: 'center', 
  justifyContent: 'center',
  minHeight: 40,
  overflow: 'hidden',
});

function RightArrow() {
  const visibility = useContext(VisibilityContext);
  const disabled = visibility.useRightArrowVisible();
  return (
    <StyledArrow
      style={{ 
        visibility: disabled ? 'hidden' : 'visible'
      }}
      onClick={() => visibility.scrollNext()}
    >
      <MoreHorizIcon />
    </StyledArrow>
  );
}

function LeftArrow() {
  const visibility = useContext(VisibilityContext);
  const disabled = visibility.useLeftArrowVisible();
  return (
    <StyledArrow
      style={{ 
        visibility: disabled ? 'hidden' : 'visible'
      }}
      onClick={() => visibility.scrollPrev()}
    >
      <MoreHorizIcon />
    </StyledArrow>
  );
}

const StyledArrow = styled('button')(({ dir }) => ({
  border: 'none',
  background: 'white',
  fontSize: '1em',
}));

const NoScrollbar = styled('div')({
  '&:hover': {
    cursor: 'grab'
  },
  '&:active': {
    cursor: 'grabbing'
  },
  '& .react-horizontal-scrolling-menu--scroll-container::-webkit-scrollbar': {
    display: 'none',
  },
  '& .react-horizontal-scrolling-menu--scroll-container': {
    scrollbarWidth: 'none',
    'msOverflowStyle': 'none',
  },
});


class DragDealer {
  clicked;
  dragging;
  position;

  constructor() {
    this.clicked = false;
    this.dragging = false;
    this.position = 0;
  }

  dragStart = (ev) => {
    this.position = ev.clientX;
    this.clicked = true;
  };

  dragStop = () => {
    window.requestAnimationFrame(() => {
      this.dragging = false;
      this.clicked = false;
    });
  };

  dragMove = (ev, cb) => {
    const newDiff = this.position - ev.clientX;

    const movedEnough = Math.abs(newDiff) > 5;

    if (this.clicked && movedEnough) {
      this.dragging = true;
    }

    if (this.dragging && movedEnough) {
      this.position = ev.clientX;
      cb(newDiff);
    }
  };
}

function onWheel(apiObj, ev) {
  // NOTE: no good standart way to distinguish touchpad scrolling gestures
  // but can assume that gesture will affect X axis, mouse scroll only Y axis
  // of if deltaY too small probably is it touchpad
  const isThouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15;

  if (isThouchpad) {
    ev.stopPropagation();
    return;
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext();
  } else {
    apiObj.scrollPrev();
  }
}


export default CanvasControls;